/**
 * Trigger a client-side browser download of a markdown string as a `.md` file.
 * Tries `msSaveOrOpenBlob` → `showSaveFilePicker` → anchor-click. No-ops on SSR.
 */
export const downloadMarkdownFile = (markdown: string, filename: string): void => {
	if (
		typeof document === 'undefined' ||
		typeof URL === 'undefined' ||
		typeof Blob === 'undefined'
	) {
		return;
	}

	const safeFilename = sanitizeFilename(filename);

	const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
	if (nav && typeof nav.msSaveOrOpenBlob === 'function') {
		const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
		nav.msSaveOrOpenBlob(blob, safeFilename);
		return;
	}

	const showSaveFilePicker =
		typeof window !== 'undefined' ? (window as any).showSaveFilePicker : undefined;
	if (typeof showSaveFilePicker === 'function') {
		void (async () => {
			try {
				const handle = await showSaveFilePicker({
					suggestedName: safeFilename,
					types: [{ description: 'Markdown file', accept: { 'text/markdown': ['.md'] } }],
				});
				const writable = await handle.createWritable();
				await writable.write(markdown);
				await writable.close();
			} catch (err) {
				// Only fall through to the anchor strategy for real errors;
				// a user-initiated cancel must be a no-op.
				if (!(err instanceof DOMException && err.name === 'AbortError')) {
					fallbackAnchorDownload(markdown, safeFilename);
				}
			}
		})();
		return;
	}

	fallbackAnchorDownload(markdown, safeFilename);
};

const fallbackAnchorDownload = (markdown: string, safeFilename: string): void => {
	// `File` carries a `name` field used as the saved filename when the
	// `download` attribute is suppressed. `application/octet-stream` forces
	// the browser to save instead of navigate.
	const file = new File([markdown], safeFilename, { type: 'application/octet-stream' });
	const url = URL.createObjectURL(file);
	// `#<filename>` hash so the URL path itself reads as the intended name
	// when both `download` and `File.name` are ignored.
	const urlWithHint = `${url}#${encodeURIComponent(safeFilename)}`;

	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const anchor = document.createElement('a');
	anchor.href = urlWithHint;
	anchor.download = safeFilename;
	anchor.target = '_self';
	anchor.rel = '';
	anchor.style.display = 'none';
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document.body.appendChild(anchor);

	const clickEvent = new MouseEvent('click', {
		view: typeof window !== 'undefined' ? window : undefined,
		bubbles: true,
		cancelable: true,
	});
	anchor.dispatchEvent(clickEvent);
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document.body.removeChild(anchor);

	setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Strip illegal/control chars, cap length, always end in `.md`. */
export const sanitizeFilename = (rawTitle: string): string => {
	const trimmed = (rawTitle || '').trim();
	const fallback = trimmed.length > 0 ? trimmed : 'page';
	const cleaned = fallback
		// eslint-disable-next-line no-control-regex
		.replace(/[\\/:*?"<>|\u0000-\u001F]/g, '-')
		.replace(/\s+/g, ' ')
		.replace(/-+/g, '-')
		.slice(0, 120)
		// Re-trim after slice so a truncation cut mid-run doesn't leave a
		// trailing dot/dash/space.
		.replace(/^[.\-\s]+|[.\-\s]+$/g, '');
	const safe = cleaned.length > 0 ? cleaned : 'page';
	return `${safe}.md`;
};
